import _ from 'lodash'
import * as THREE from 'three'

import { EditorUserError } from '@glass-designer/utils/editorErrors'
import BaseTool from '@glass-designer/tools/baseTool'

import EditingCutout from '@glass-designer/objects/editingCutout'
import EditingItem from '@glass-designer/objects/editingPoint'
import DatumLink from '@glass-designer/model/datumLink'

import GlassDesignerTypes from '@glass-designer/types'
import EditingPoint from '@glass-designer/objects/editingPoint'


// Shape Expand/Shrink Tool
// Called when 'Expand' or 'Shrink' selected when a shape is selected
export default class ShapeExpandShrinkTool extends BaseTool {
  constructor(editor) {
    super(editor)

    this.type = GlassDesignerTypes.ToolTypes.shapeExpandShrinkTool
    this.selectedShape = null
    this.widthAdjust = 0
    this.heightAdjust = 0
    this.isExpandMode = false
    this.isShrinkMode = false
  }


  // Tool Active
  // Called when the tool is active
  onToolActive(options = {}) {
    super.onToolActive()

    this.selectedShape = options.glassShape
    this.widthAdjust = 0
    this.heightAdjust = 0
    this.isExpandMode = options.isExpandMode || false
    this.isShrinkMode = options.isShrinkMode || false
    
    this.displayToolSettingsPopover()
  }

  // Tool Inactive
  // Called when the tool is no longer active
  onToolInactive() {
    super.onToolInactive()

    this.editor.component.hidePopover()

    this.editor.resetDimensionsEnabled()
    this.editor.resetSelectionEnabled()
  }


  displayToolSettingsPopover() {
    // Fill in options
    var popoverOptions = {
      widthAdjust: this.widthAdjust,
      heightAdjust: this.heightAdjust,
      isExpandMode: this.isExpandMode,
      isShrinkMode: this.isShrinkMode,
      context: this,
      updateCallback: (context, options) => {
        context.updateEditValues(options)
      },
      cancelCallback: (context, userCancelled) => {
        if(userCancelled)
          context.closeTool()
      },
      applyCallback: (context, options) => {
        context.applyExpandShrinkEdit(options)
      },
    }

    var displayOptions = {
      horizontalPosition: this._calculatePopoverHorizontalPosition()
    }

    this.editor.component.displayExpandShrinkToolPopover(popoverOptions, displayOptions)
  }


  // Update tool options
  updateEditValues(options) {
    this.widthAdjust = Math.abs(options.widthAdjust)
    this.heightAdjust = Math.abs(options.heightAdjust)
  }

  // Apply point slicing to the selected segment
  applyExpandShrinkEdit(options) {
    this.widthAdjust = Math.abs(options.widthAdjust)
    this.heightAdjust = Math.abs(options.heightAdjust)

    this.editor.startUndoGroup(this.isExpandMode ? 'Expanding shape' : 'Shrinking shape')
    this.editor.addUndoShape(this.selectedShape)

    try {
      if(this.isShrinkMode) {
        this.selectedShape.expandShape(-this.widthAdjust, -this.heightAdjust)
      } else {
        this.selectedShape.expandShape(this.widthAdjust, this.heightAdjust)
      }
    } catch(err) {
      this.editor.cancelCurrentUndoGroup()

      // Display error
      if(err instanceof EditorUserError)
        this.editor.component.errorMessage('Error editing shape', err.message)
      
      return
    }    

    this.editor.closeCurrentUndoGroup()

    // Rebuild dimensions
    this.editor.resetDimensionsEnabled()
    this.editor.resetSelectionEnabled()
    this.editor.onShapeUpdated(this.selectedShape)

    this.closeTool()
  }


  /**
   * Update the editing points for the segment
   * Only updates if point count changed
   * If update required, subdivides the segment equally for each point
   */
  _updateEditingPoints(pointCount) {
    if(pointCount == this.editingPoints.length)
      return

    this.datumLinks = []

    // Add/Remove points
    let removeCount = Math.max(this.editingPoints.length - pointCount, 0)
    let addCount = Math.max(pointCount - this.editingPoints.length, 0)

    while(removeCount-- > 0) {
      let editingPnt = this.editingPoints.pop()
      editingPnt.dispose()
    }
    while(addCount-- > 0) {
      var editingPnt = new EditingPoint(this.editor)
      this.editingPoints.push(editingPnt)
    }


    // Update points
    // Note: Does not handle arcs
    if(this.editingPoints.length > 0) {
      let segment = this.selectedShape.getSegmentForIndex(this.selectedSegmentIndex)
      let fraction = 1.0 / (this.editingPoints.length+1)

      this.editingPoints.forEach((editingPoint, index) => {
        let segFraction = (index+1) * fraction
        
        editingPoint.setPosition(segment.getPoint(segFraction))

        var datumLink = new DatumLink()
        datumLink.update({
          sourceShapeId: this.selectedShape.id,
          segmentId: segment.id,
          sourceDatumType: GlassDesignerTypes.DatumTypes.position,
          sourcePosPoint: segment.getPoint(index * fraction),
          sourcePosDir: segment.getDirection(),
          sourceOffsetDirection: 1,
          sourceOffsetDistance: segment.getLength() * fraction,
          userData: { index: index }
        })

        this.datumLinks.push(datumLink)

        // Last datum link
        if(index == this.editingPoints.length-1) {
          datumLink = new DatumLink()
          datumLink.update({
            sourceShapeId: this.selectedShape.id,
            segmentId: segment.id,
            sourceDatumType: GlassDesignerTypes.DatumTypes.position,
            sourcePosPoint: editingPoint.position,
            sourcePosDir: segment.getDirection(),
            sourceOffsetDirection: 1,
            sourceOffsetDistance: segment.getLength() * fraction,
            userData: { index: index+1 }
          })
          this.datumLinks.push(datumLink)
        }
      })

      this.editingPoints.forEach((editingPoint, index) => {
        editingPoint.setDatumLinks(_.compact([index == 0 ? this.datumLinks[index] : null, this.datumLinks[index+1]]))
      })
    }

    this.editor.onToolEditingUpdated(this.selectedShape, this.editingPoints)
    this.editor.render()
  }

}
